import { useCallback, useMemo, useState } from 'react'
import type { NewDeal, Product, Totals } from 'types'
import { calculateDealTotals, monthsFor, updateProductField as calcUpdateProductField, normalizeProduct } from 'utils/dealCalculations'
import { toMoney } from 'utils/toMoney'

const MONEY_FIELDS = new Set(['price', 'margin', 'billedPrice', 'mediaSpend', 'total', 'netTotal'])

interface UseAmendmentProductsArgs {
  sourceProducts: Product[]
  deal: NewDeal
  changeType: 'major' | 'minor' | 'patch'
  keepIdentity?: boolean
}

interface UseAmendmentProductsReturn {
  draftProducts: Product[]
  handleProductChange: (index: number, field: string, value: any, extra?: any) => void
  handleRemoveRow: (sk: string) => void
  handleAddProduct: () => void
  handleDragEnd: (result: any) => void
  syncAllProductDates: (startDate: string, endDate: string) => void
  updateProductStartDates: (startDate: string) => void
  currentTotals: Totals
  newTotals: Totals
  validationErrors: Record<string, boolean>
  validateAll: () => boolean
}

/**
 * Strip identity fields from products so they get new IDs on save.
 */
function cloneProductsForDraft(products: Product[]): Product[] {
  return products.map((p, index) => {
    const uid = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}-${index}`
    return {
      ...p,
      PK: undefined as any,
      SK: `PRODUCT_${uid}`,
      deal_uuid: undefined as any,
      instanceId: uid,
      rowId: uid,
      createdAt: undefined as any,
      sortOrder: index,
    }
  })
}

export function useAmendmentProducts({
  sourceProducts,
  deal,
  changeType,
  keepIdentity = changeType === 'patch',
}: UseAmendmentProductsArgs): UseAmendmentProductsReturn {
  const [draftProducts, setDraftProducts] = useState<Product[]>(() =>
    keepIdentity
      ? sourceProducts.map((p, i) => ({ ...p, sortOrder: i })) // keep PK/SK/deal_uuid for in-place update
      : cloneProductsForDraft(sourceProducts)
  )
  const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({ products: false })

  // Current totals (from original products — computed once)
  const currentTotals = useMemo(() => calculateDealTotals(deal, sourceProducts), [deal, sourceProducts])

  // New totals (from draft products — recomputed on every change)
  const newTotals = useMemo(() => calculateDealTotals(deal, draftProducts), [deal, draftProducts])

  const handleProductChange = useCallback(
    (index: number, field: string, value: any, extra?: any) => {
      // Block edits to derived fields
      if (field === 'mediaSpend' || field === 'grossProfit' || field === 'billedPrice') return

      const normalized = MONEY_FIELDS.has(field) ? toMoney(value) : value
      setDraftProducts(prev => {
        const currentRow = prev[index]
        if (!currentRow) return prev

        const extras = extra ?? {}
        const clientMargin = field === 'payer' && value === 'Client' ? 15 : 0

        const updated = calcUpdateProductField(currentRow, field as keyof Product, normalized, extras, clientMargin)

        const next = [...prev]
        next[index] = updated
        return next
      })
    },
    []
  )

  const handleRemoveRow = useCallback((sk: string) => {
    setDraftProducts(prev => prev.filter(p => p.SK !== sk && p.instanceId !== sk))
  }, [])

  const handleAddProduct = useCallback(() => {
    const uid = crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`
    const maxOrder = draftProducts.reduce((m, p) => Math.max(m, p.sortOrder ?? -1), -1)
    const raw: Partial<Product> = {
      SK: `PRODUCT_${uid}`,
      instanceId: uid,
      rowId: uid,
      sortOrder: maxOrder + 1,
      productName: '',
      qty: 1,
      price: 0,
      margin: 0,
      payer: 'GRO',
      accountUsed: 'GRO',
      startDate: deal?.startDate as any,
      endDate: deal?.endDate as any,
      dateRange: [deal?.startDate, deal?.endDate] as any,
      createdAt: new Date().toISOString(),
      EntityType: 'product',
      status: '',
    }
    setDraftProducts(prev => [...prev, normalizeProduct(raw as Product)])
  }, [draftProducts, deal])

  const handleDragEnd = useCallback(
    (result: any) => {
      const { source, destination } = result
      if (!destination || source.index === destination.index) return
      setDraftProducts(prev => {
        const next = [...prev]
        const [moved] = next.splice(source.index, 1)
        next.splice(destination.index, 0, moved)
        return next.map((p, i) => ({ ...p, sortOrder: i }))
      })
    },
    []
  )

  const syncAllProductDates = useCallback((startDate: string, endDate: string) => {
    setDraftProducts(prev =>
      prev.map(p => {
        const qty = monthsFor(startDate, endDate)
        const updated = calcUpdateProductField(
          { ...p, startDate, endDate, dateRange: [startDate, endDate] },
          'qty' as keyof Product,
          qty,
          {},
          0
        )
        return updated
      })
    )
  }, [])

  const updateProductStartDates = useCallback((startDate: string) => {
    setDraftProducts(prev =>
      prev.map(p => {
        const endDate = (p.endDate as string) || ''
        const qty = endDate ? monthsFor(startDate, endDate) : (p.qty ?? 1)
        const updated = calcUpdateProductField(
          { ...p, startDate, dateRange: [startDate, endDate || p.endDate] },
          'qty' as keyof Product,
          qty,
          {},
          0
        )
        return updated
      })
    )
  }, [])

  const validateAll = useCallback(() => {
    const hasProducts = draftProducts.length > 0
    const allHaveNames = draftProducts.every(p => !!p.productName)
    const allDatesValid = draftProducts.every(p => {
      if (!p.startDate || !p.endDate) return true
      return new Date(p.endDate as string) >= new Date(p.startDate as string)
    })
    const errors = {
      products: !hasProducts || !allHaveNames,
      dates: !allDatesValid,
    }
    setValidationErrors(errors)
    return !Object.values(errors).some(Boolean)
  }, [draftProducts])

  return {
    draftProducts,
    handleProductChange,
    handleRemoveRow,
    handleAddProduct,
    handleDragEnd,
    syncAllProductDates,
    updateProductStartDates,
    currentTotals,
    newTotals,
    validationErrors,
    validateAll,
  }
}
